import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Snackbar,
  Typography,
} from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import { ChangeCircleOutlined, ChevronLeft } from '@mui/icons-material';
import { Loader } from '../common/Loader';
import { useClinic } from '../../hooks/useClinic';
import { getPostImageUrl } from '../../services/clinicService';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';

const decodeHtmlEntities = (text: string): string => {
  const div = document.createElement('div');
  div.innerHTML = text;
  return div.textContent || div.innerText || '';
};

const formatWorkSchedule = (schedule: any): string => {
  if (!schedule) return 'Не указан';
  if (schedule.is24_7) return 'Круглосуточно';
  
  const DAY_NAMES_FULL: Record<string, string> = {
    monday: 'Пн', tuesday: 'Вт', wednesday: 'Ср', thursday: 'Чт',
    friday: 'Пт', saturday: 'Сб', sunday: 'Вс',
  };
  
  const DAYS_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  
  const daysInfo = DAYS_ORDER.map(dayKey => {
    const day = schedule[dayKey];
    if (!day || !day.isWorking) {
      return { label: DAY_NAMES_FULL[dayKey], isWorking: false, hours: null };
    }
    if (day.is24_7) {
      return { label: DAY_NAMES_FULL[dayKey], isWorking: true, hours: 'Круглосуточно' };
    }
    return { 
      label: DAY_NAMES_FULL[dayKey], 
      isWorking: true, 
      hours: `${day.startTime}–${day.endTime}`
    };
  });
  
  const groups: { days: string[]; hours: string }[] = [];
  
  for (let i = 0; i < daysInfo.length; i++) {
    const current = daysInfo[i];
    
    if (!current.isWorking) {
      groups.push({ days: [current.label], hours: 'Выходной' });
      continue;
    }
    
    if (groups.length === 0 || groups[groups.length - 1].hours !== current.hours) {
      groups.push({ days: [current.label], hours: current.hours! });
    } else {
      groups[groups.length - 1].days.push(current.label);
    }
  }
  
  return groups.map(group => {
    let daysStr = '';
    if (group.days.length === 1) {
      daysStr = group.days[0];
    } else if (group.days.length === 2) {
      daysStr = `${group.days[0]}, ${group.days[1]}`;
    } else {
      daysStr = `${group.days[0]}-${group.days[group.days.length - 1]}`;
    }
    return `${daysStr}: ${group.hours}`;
  }).join('\n');
};

export const ClinicInfoList: React.FC = () => {
  const navigate = useNavigate();
  const [activeInfoTab, setActiveInfoTab] = useState(0); // 0 - Новости, 1 - Контакты, 2 - Как добраться
  const [selectedNewsPost, setSelectedNewsPost] = useState<any | null>(null);
  const [showNewsDetail, setShowNewsDetail] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);

  const { user } = useAuthStore();
  const currentBranchId = user?.branchId;

  const {
    branches,
    news,
    isLoading,
    error,
  } = useClinic();

  const currentBranch = branches.find((branch) => branch.id === currentBranchId);
  const currentBranchName = currentBranch?.name || 'Филиал не выбран';

  // Восстанавливаем позицию скролла при возврате из детальной новости
  useEffect(() => {
    if (!showNewsDetail) {
      window.scrollTo(0, scrollPosition);
    }
  }, [showNewsDetail, scrollPosition]);

  const handleNewsClick = (post: any) => {
    setScrollPosition(window.scrollY);
    setSelectedNewsPost(post);
    setShowNewsDetail(true);
    window.scrollTo(0, 0);
  };

  if (isLoading) {
    return <Loader message="Загрузка..." />;
  }

  if (error) {
    return (
      <Paper sx={{ padding: 2, backgroundColor: '#ffebee' }}>
        <Typography color="error">Ошибка: {error}</Typography>
      </Paper>
    );
  }

  // Рендер детальной новости (отдельная страница)
  if (showNewsDetail && selectedNewsPost) {
    return (
      <Box sx={{ pb: 12 }}>
        <Box
          onClick={() => setShowNewsDetail(false)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            mb: 2,
            ml: '-4px',
            color: '#007AFF',
            cursor: 'pointer',
            userSelect: 'none',
            '&:active': { opacity: 0.6 }
          }}
        >
          <ChevronLeft sx={{ fontSize: 16, mr: '-2px', ml: '-2px', mt: '1px' }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
            Назад
          </Typography>
        </Box>

        {/* Картинка размером 359x244, только закругление, без обрезки */}
        {selectedNewsPost.image && (
          <Box
            sx={{
              width: '359px',
              height: '244px',
              borderRadius: '30px',
              overflow: 'hidden',
              mb: 2,
              margin: '0 auto 16px auto',
            }}
          >
            <img
              src={getPostImageUrl(selectedNewsPost.image)!}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain', // без обрезки, картинка полностью видна
                display: 'block',
              }}
            />
          </Box>
        )}

        {/* Текст на фоне, выровнен по левому краю */}
        <Typography
          sx={{
            fontFamily: 'TikTok Sans Medium, sans-serif',
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.4,
            color: '#212121',
            textAlign: 'left',
            pb: 3,
          }}
        >
          {decodeHtmlEntities(selectedNewsPost.content)}
        </Typography>
      </Box>
    );
  }

  // Основной рендер
  return (
    <Box>
      {/* Кнопка "Назад" на главную */}
      <Box
        onClick={() => navigate('/')}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          mb: 1.5,
          color: '#007AFF',
          cursor: 'pointer',
          userSelect: 'none',
          '&:active': { opacity: 0.6 }
        }}
      >
        <ChevronLeft sx={{ fontSize: 16, mr: '-2px', ml: '-2px', mt: '1px' }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1 }}>
          Назад
        </Typography>
      </Box>

      {/* Унифицированный контейнер: заголовок, филиал, вкладки */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '768px' } }}>
          {/* Заголовок */}
          <Typography sx={{ fontSize: { xs: 28, sm: 32, md: 36 }, fontWeight: 600, mb: 1, textAlign: 'left' }}>
            О клинике
          </Typography>

          {/* Строка с филиалом и кнопкой изменения */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography sx={{ fontSize: { xs: 14, sm: 15, md: 16 }, color: '#666' }}>
              {currentBranchName}
            </Typography>
            <Button
              onClick={() => navigate('/profile', { state: { from: 'clinic-info' } })}
              sx={{ color: '#007AFF', textTransform: 'none', fontSize: { xs: 13, sm: 14 }, p: 0, minWidth: 'auto', gap: 0.5, '& svg': { width: { xs: 20, md: 24 }, height: { xs: 20, md: 24 } } }}
            >
              Изменить филиал  <ChangeCircleOutlined />
            </Button>
          </Box>

          {/* Три кнопки-вкладки */}
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
            {['Новости', 'Контакты', 'Как добраться'].map((label, index) => (
              <Button
                key={label}
                onClick={() => setActiveInfoTab(index)}
                sx={{
                  borderRadius: '30px',
                  px: { xs: 2, sm: 2.5, md: 3 },
                  py: { xs: 0.5, sm: 0.75 },
                  backgroundColor: activeInfoTab === index ? '#007AFF' : '#F0F0F0',
                  color: activeInfoTab === index ? '#FFFFFF' : '#666666',
                  textTransform: 'none',
                  fontSize: { xs: 13, sm: 14 },
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  border: activeInfoTab === index ? 'none' : '1px solid rgba(33, 33, 33, 0.3)',
                  '&:hover': {
                    backgroundColor: activeInfoTab === index ? '#007AFF' : '#E0E0E0',
                  },
                }}
              >
                {label}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Вкладка Новости */}
      {activeInfoTab === 0 && (
        <Box>
          {news.length === 0 ? (
            <Paper sx={{ padding: 3, textAlign: 'center', borderRadius: '16px' }}>
              <Typography color="textSecondary">Нет новостей</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2} sx={{ justifyContent: 'center' }}>
              {news.map((post) => (
                <Grid item xs={12} sm={6} md={6} lg={4} key={post.id}>
                  <Card
                    onClick={() => handleNewsClick(post)}
                    sx={{
                      width: '100%',
                      borderRadius: '16px',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      background: '#fff',
                      transition: '0.2s',
                      margin: '0 auto',
                      '&:active': { transform: 'scale(0.98)' }
                    }}
                  >
                    {post.image && (
                      <Box sx={{ width: '100%', height: '170px' }}>
                        <img
                          src={getPostImageUrl(post.image)!}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    )}
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 13, sm: 14 },
                          color: '#212121',
                          lineHeight: 1.4,
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 6,
                          WebkitBoxOrient: 'vertical',
                          mb: 1,
                        }}
                      >
                        {decodeHtmlEntities(post.content || '')}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <Typography sx={{ color: '#007AFF', fontWeight: 600, fontSize: { xs: 13, sm: 14 } }}>
                          Подробнее
                        </Typography>
                        <ArrowOutwardIcon sx={{ color: '#007AFF', fontSize: { xs: 16, sm: 18 } }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

        {/* Вкладка Контакты */}
        {activeInfoTab === 1 && currentBranch && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: '16px', bgcolor: '#FFFFFF', width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' } }}>
              {/* Название филиала */}
              <Typography sx={{ fontWeight: 600, fontSize: { xs: 16, sm: 18 }, mb: 2, lineHeight: 1.2 }}>
                {currentBranch.name}
              </Typography>

              {/* Телефон */}
              <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 500, color: 'rgba(33, 33, 33, 0.7)', mb: 0.5, lineHeight: 1.2 }}>
                Телефон
              </Typography>
              <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 500, color: '#212121', mb: 2, lineHeight: 1.2 }}>
                {currentBranch.phone || 'Не указан'}
              </Typography>

              {/* Адрес */}
              <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 500, color: 'rgba(33, 33, 33, 0.7)', mb: 0.5, lineHeight: 1.2 }}>
                Адрес
              </Typography>
              <Typography sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 500, color: '#212121', mb: 2, lineHeight: 1.2 }}>
                {currentBranch.address || 'Не указан'}
              </Typography>

              {/* Время работы */}
              <Typography sx={{ fontSize: { xs: 12, sm: 13 }, fontWeight: 500, color: 'rgba(33, 33, 33, 0.7)', mb: 0.5, lineHeight: 1.2 }}>
                Время работы
              </Typography>
              <Box sx={{ mb: 2 }}>
                {formatWorkSchedule(currentBranch.workSchedule).split('\n').map((line, index) => {
                  return (
                    <Typography key={index} sx={{ fontSize: { xs: 14, sm: 16 }, fontWeight: 500, color: '#212121', lineHeight: 1.2 }}>
                      {line}
                    </Typography>
                  );
                })}
              </Box>

              {/* Кнопка "Записаться на приём" */}
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate('/book-appointment')}
                sx={{
                  height: { xs: 44, sm: 48 },
                  borderRadius: '24px',
                  backgroundColor: '#007AFF',
                  textTransform: 'none',
                  fontSize: { xs: 14, sm: 15 },
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: '#005FCC'
                  }
                }}
              >
                Записаться на приём
              </Button>
            </Paper>
          </Box>
        )}

      {/* Вкладка Как добраться */}
      {activeInfoTab === 2 && currentBranch && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Paper sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: '16px', width: '100%', maxWidth: { xs: '100%', sm: '480px', md: '600px' } }}>
            <Typography sx={{ fontWeight: 600, fontSize: { xs: 16, sm: 18 }, mb: 2, lineHeight: 1.1 }}>
              {currentBranch.name}
            </Typography>

            {/* Map Image */}
            {getPostImageUrl(currentBranch.mapImageName) && (
              <Box
                sx={{
                  width: '100%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  mb: 2,
                  backgroundColor: '#E8E8E8',
                }}
              >
                <img
                  src={getPostImageUrl(currentBranch.mapImageName)!}
                  alt={currentBranch.name}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'cover',
                  }}
                />
              </Box>
            )}

            <Typography sx={{ fontSize: { xs: 13, sm: 14 }, color: '#666', whiteSpace: 'pre-line', mb: 2, lineHeight: 1.3 }}>
              {currentBranch.howToGetDescription || 'Информация о том, как добраться, не указана.'}
            </Typography>

            {/* Кнопка "Записаться на приём" */}
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/book-appointment')}
              sx={{
                height: { xs: 44, sm: 48 },
                borderRadius: '24px',
                backgroundColor: '#007AFF',
                textTransform: 'none',
                fontSize: { xs: 14, sm: 15 },
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#005FCC'
                }
              }}
            >
              Записаться на приём
            </Button>
          </Paper>
        </Box>
      )}

    </Box>
  );
};
